import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import SEO from '../components/SEO';
import {
 ArrowLeft,
 Store,
 User,
 Mail,
 Lock,
 Phone,
 MapPin,
 Hash,
 Building2,
 Map,
 Utensils,
 Users,
 ShieldCheck,
 Gift,
 CheckCircle2,
 AlertCircle,
 Loader2,
} from 'lucide-react';
import { GoldBellMark } from '../components/NoFeeLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import StripeTrustBanner from '../components/StripeTrustBanner';
import StripeRedirectOverlay from '../components/StripeRedirectOverlay';
import { detectAddressMismatch, formatUSPhone } from '../utils/addressValidation';
import { US_STATES } from '../utils/usStates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = '/register-photos/hero.png';

const CUISINE_OPTIONS = [
 { value: '', label: 'Restaurant Type' },
 { value: 'american', label: 'American' },
 { value: 'brazilian', label: 'Brazilian' },
 { value: 'mexican', label: 'Mexican' },
 { value: 'italian', label: 'Italian' },
 { value: 'asian', label: 'Asian' },
 { value: 'japanese', label: 'Japanese' },
 { value: 'chinese', label: 'Chinese' },
 { value: 'indian', label: 'Indian' },
 { value: 'mediterranean', label: 'Mediterranean' },
 { value: 'latin', label: 'Latin / Hispanic' },
 { value: 'pizza', label: 'Pizza' },
 { value: 'burger', label: 'Burgers' },
 { value: 'seafood', label: 'Seafood' },
 { value: 'bakery', label: 'Bakery & Coffee' },
 { value: 'bar', label: 'Bar & Grill' },
 { value: 'other', label: 'Other' },
];

const HOW_HEARD_OPTIONS = [
 { value: '', label: 'How did you hear about NoFee?' },
 { value: 'friend', label: 'Friend / Word of mouth' },
 { value: 'google', label: 'Google search' },
 { value: 'social', label: 'Social media (Instagram, TikTok…)' },
 { value: 'ambassador', label: 'NoFee Ambassador' },
 { value: 'event', label: 'Restaurant event / Trade show' },
 { value: 'press', label: 'Press / News article' },
 { value: 'other', label: 'Other' },
];

// Logo with full tagline — dark variant for white backgrounds
const RegisterLogo = () => (
 <div className="flex items-center gap-3" data-testid="register-logo">
 <GoldBellMark size={56} />
 <div className="leading-none">
 <div className="font-black tracking-tight" style={{ fontSize: '40px' }}>
 <span className="text-zinc-900">No</span>
 <span className="bg-gradient-to-b from-gold-400 via-gold-500 to-gold-700 bg-clip-text text-transparent">Fee</span>
 </div>
 <div className="mt-1.5 text-[10px] font-bold tracking-[0.18em] flex gap-1.5">
 <span className="text-zinc-900">MORE ORDERS.</span>
 <span className="text-zinc-900">MORE PROFIT.</span>
 <span className="text-gold-600">NO FEES.</span>
 </div>
 </div>
 </div>
);

const Field = ({ icon: Icon, children }) => (
 <div className="relative">
 <Icon className="w-5 h-5 text-neutral-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
 {children}
 </div>
);

const RegisterPage = () => {
 const { register } = useAuth();
 const navigate = useNavigate();
 const { t } = useTranslation();
 const [params] = useSearchParams();

 // Localize the first (empty value) option in each select
 const cuisineOptions = [
 {...CUISINE_OPTIONS[0], label: t('register.restaurant_type') },
...CUISINE_OPTIONS.slice(1),
 ];
 const howHeardOptions = [
 {...HOW_HEARD_OPTIONS[0], label: t('register.how_heard') },
...HOW_HEARD_OPTIONS.slice(1),
 ];

 const [form, setForm] = useState({
 restaurant_name: '',
 name: '',
 email: '',
 password: '',
 phone: '',
 address: '',
 zip_code: '',
 city: '',
 state: '',
 cuisine_type: '',
 cuisine_type_other: '',
 how_heard: '',
 referral_code: (params.get('ref') || '').toUpperCase(),
 partner_code: (params.get('partner') || '').toUpperCase(),
 });
 const [acceptedTerms, setAcceptedTerms] = useState(false);
 const [error, setError] = useState('');
 const [loading, setLoading] = useState(false);
 const [redirecting, setRedirecting] = useState(false);
 const [refStatus, setRefStatus] = useState(null);
 const [refChecking, setRefChecking] = useState(false);
 const [partnerInfo, setPartnerInfo] = useState(null);
 // Per-field validation errors. We disable the browser's native
 // `Fill out this field` popup (which renders as a near-invisible
 // tooltip on some platforms) and show our own red error message
 // directly under each field instead.
 const [fieldErrors, setFieldErrors] = useState({});
 const clearFieldError = (key) => setFieldErrors((prev) => {
 if (!prev[key]) return prev;
 const next = {...prev };
 delete next[key];
 return next;
 });
 // ZIP → city/state resolution (same Zippopotam.us-backed endpoint the
 // customer onboarding and restaurant settings use). Blocks submission
 // until we have a valid US ZIP so the partner-referral signup can't
 // accept a Boston address with a Florida ZIP.
 const [zipLookup, setZipLookup] = useState({ status: 'idle', city: '', state: '', error: '' });

 const goesToBilling = params.get('next') === 'billing';

 // Debounced ZIP lookup — fires once the field has exactly 5 digits.
 useEffect(() => {
 const zip = (form.zip_code || '').trim();
 let cancelled = false;
 const t = setTimeout(() => {
 const run = async () => {
 if (!/^\d{5}$/.test(zip)) {
 setZipLookup({ status: 'idle', city: '', state: '', error: '' });
 return;
 }
 setZipLookup({ status: 'loading', city: '', state: '', error: '' });
 try {
 const r = await axios.get(`${API}/public/zip-lookup/${encodeURIComponent(zip)}`);
 if (cancelled) return;
 if (r.data?.valid) {
 const city = r.data.city || '';
 const state = r.data.state || '';
 setZipLookup({ status: 'ok', city, state, error: '' });
 // Auto-populate the city/state fields so the user can see what
 // the ZIP resolved to. Owner can still edit if needed.
 setForm((f) => ({...f, city, state }));
 // Resolving the ZIP also satisfies the ZIP/city/state
 // required-field errors — clear them so they don't keep
 // showing red after the user supplies a valid ZIP.
 setFieldErrors((prev) => {
 const next = {...prev };
 delete next.zip_code;
 delete next.city;
 delete next.state;
 return next;
 });
 } else {
 setZipLookup({ status: 'invalid', city: '', state: '', error: 'Not a recognized US ZIP code.' });
 }
 } catch {
 if (!cancelled) setZipLookup({ status: 'invalid', city: '', state: '', error: 'Could not verify this ZIP code.' });
 }
 };
 run();
 }, 350);
 return () => { cancelled = true; clearTimeout(t); };
 }, [form.zip_code]);

 const addressMismatch =
 zipLookup.status === 'ok'
? detectAddressMismatch(form.address, (zipLookup.state || '').toUpperCase(), (zipLookup.city || '').toLowerCase())
: null;

 // Look up partner organization name when?partner=CODE is present so the
 // signup form can show a trust banner ("Referred by Boston Pizza Association
 // — 45 days free"). Failure is silent — partner_code still flows to backend.
 useEffect(() => {
 const code = (form.partner_code || '').trim();
 if (!code) {
 setPartnerInfo(null);
 return;
 }
 let cancelled = false;
 axios
.get(`${API}/partners/lookup/${encodeURIComponent(code)}`)
.then((r) => { if (!cancelled) setPartnerInfo(r.data); })
.catch(() => { if (!cancelled) setPartnerInfo({ invalid: true }); });
 return () => { cancelled = true; };
 }, [form.partner_code]);

 useEffect(() => {
 const code = form.referral_code.trim();
 if (!code) {
 setRefStatus(null);
 return;
 }
 setRefChecking(true);
 const t = setTimeout(() => {
 axios
.get(`${API}/rewards/code/${encodeURIComponent(code)}/validate`)
.then((r) => setRefStatus(r.data))
.catch(() => setRefStatus({ valid: false }))
.finally(() => setRefChecking(false));
 }, 400);
 return () => clearTimeout(t);
 }, [form.referral_code]);

 const handleSubmit = async (e) => {
 e.preventDefault();
 setError('');
 // Validate every required field client-side. We collect ALL the
 // errors first so the form shows each missing field in red at once.
 const errs = {};
 if (!form.restaurant_name.trim()) errs.restaurant_name = t('register.required_field', { defaultValue: 'Required field' });
 if (!form.name.trim()) errs.name = t('register.required_field', { defaultValue: 'Required field' });
 if (!form.email.trim() ||!/^\S+@\S+\.\S+$/.test(form.email)) errs.email = t('register.required_valid_email', { defaultValue: 'Enter a valid email' });
 if (!form.password || form.password.length < 6) errs.password = t('register.required_password', { defaultValue: 'Minimum 6 characters' });
 if (!form.phone.trim()) errs.phone = t('register.required_field', { defaultValue: 'Required field' });
 if (!form.address.trim()) errs.address = t('register.required_field', { defaultValue: 'Required field' });
 if (!form.zip_code ||!/^\d{5}$/.test(form.zip_code.trim())) {
 errs.zip_code = t('register.required_zip', { defaultValue: 'Enter a valid 5-digit US ZIP code' });
 }
 if (!form.city.trim()) errs.city = t('register.required_field', { defaultValue: 'Required field' });
 if (!form.state.trim()) errs.state = t('register.required_state', { defaultValue: 'Select your state' });
 if (form.cuisine_type === 'other' &&!form.cuisine_type_other.trim()) {
 errs.cuisine_type_other = t('register.required_field', { defaultValue: 'Required field' });
 }
 if (!acceptedTerms) errs._terms = t('register.accept_terms_required');

 if (Object.keys(errs).length > 0) {
 setFieldErrors(errs);
 // Scroll to the first error so the user immediately sees what's missing.
 const firstKey = Object.keys(errs).find((k) => k!== '_terms');
 if (firstKey) {
 const el = document.querySelector(`[data-testid=register-${firstKey.replace('_', '-')}]`)
 || document.querySelector(`[data-testid=register-${firstKey}]`);
 if (el && typeof el.scrollIntoView === 'function') {
 el.scrollIntoView({ behavior: 'smooth', block: 'center' });
 }
 if (el && typeof el.focus === 'function') el.focus();
 }
 if (errs._terms) setError(errs._terms);
 return;
 }
 setFieldErrors({});

 // ZIP-state sanity (only relevant after we know the ZIP resolved OK).
 if (zipLookup.status === 'loading') {
 setError(t('register.zip_verifying', { defaultValue: 'Verifying your ZIP code — please wait a moment.' }));
 return;
 }
 if (zipLookup.status!== 'ok') {
 setError(zipLookup.error || t('register.zip_invalid', { defaultValue: 'Please enter a valid US ZIP code.' }));
 return;
 }
 if (addressMismatch) {
 setError(addressMismatch.message);
 return;
 }
 // Real-address geocoding — catches typos / non-existent street names.
 if (form.address && form.address.trim().length >= 3) {
 try {
 const v = await axios.post(`${API}/public/validate-address`, {
 street: form.address.trim(),
 city: form.city || zipLookup.city,
 state: form.state || zipLookup.state,
 zip_code: form.zip_code.trim(),
 });
 if (!v.data?.valid) {
 setError(v.data?.error_message || t('register.address_invalid', { defaultValue: 'Please correct your address.' }));
 return;
 }
 } catch {
 // Soft-fail on a network blip — backend re-checks anyway.
 }
 }
 setLoading(true);
 try {
 const payload = {
...form,
 // Server-side trims/normalizes too, but keep the payload clean.
 city: form.city || zipLookup.city,
 state: (form.state || zipLookup.state || '').toUpperCase(),
 };
 if (!payload.referral_code) delete payload.referral_code;
 if (!payload.partner_code) delete payload.partner_code;
 if (payload.cuisine_type!== 'other') delete payload.cuisine_type_other;
 await register(payload);

 // If user came from a pricing CTA, jump straight to Stripe Checkout
 const next = params.get('next');
 if (next === 'billing') {
 setRedirecting(true);
 try {
 const token = localStorage.getItem('nofee_token');
 const origin = window.location.origin;
 const res = await axios.post(
 `${API}/billing/create-checkout-session`,
 { origin_url: origin },
 { headers: { Authorization: `Bearer ${token}` } }
 );
 if (res.data?.url) {
 // Brief moment to let the user see the elegant handoff overlay
 setTimeout(() => {
 window.location.href = res.data.url;
 }, 900);
 return;
 }
 } catch (billingErr) {
 // Fallback to onboarding if checkout creation fails
 setRedirecting(false);
 console.error('Billing checkout failed after register:', billingErr);
 }
 }

 // Fast by NoFee / restaurant CTA → land in the onboarding hub so
 // owners complete the full setup BEFORE seeing the payment page.
 if (next === 'onboarding') {
 navigate('/onboarding');
 return;
 }

 navigate('/dashboard?welcome=1');
 } catch (err) {
 setError(err.response?.data?.detail || t('register.registration_failed'));
 } finally {
 setLoading(false);
 }
 };

 const input =
 'w-full pl-12 pr-4 py-3.5 rounded-xl bg-white border border-neutral-200 text-neutral-900 placeholder:text-neutral-400 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none transition-all';

 return (
 <div className="min-h-screen w-full flex flex-col md:flex-row bg-neutral-50" data-testid="register-page">
 <SEO
 title="Register Your Restaurant on NoFee — 0% Commission, 45 Days Free"
 description="Create your NoFee account in minutes. Get a custom digital menu, QR code, WhatsApp ordering, and direct Stripe payouts. 45 days free, then $299/month flat — 0% commission forever."
 path="/register"
 noindex
 />
 {redirecting && (
 <StripeRedirectOverlay message="Redirecting to Stripe for secure checkout…" />
 )}
 {/* MOBILE-ONLY HERO (below md) — shows the FULL mockup image */}
 <div className="md:hidden relative w-full bg-neutral-950 overflow-hidden">
 <img
 src={HERO_IMG}
 alt="NoFee — More Orders. More Profit. No Fees."
 className="w-full h-auto block"
 />
 </div>

 {/* LEFT — Form column (fixed narrow width so image stays uncropped) */}
 <div className="w-full md:w-[440px] lg:w-[480px] xl:w-[520px] flex-shrink-0 bg-white flex flex-col">
 <div className="flex-1 px-6 sm:px-10 md:px-10 lg:px-14 py-8 lg:py-10 overflow-y-auto">
 {/* Smart back — respects browser history. Falls back to /
 ONLY when there is no in-app history (e.g. the user opened
 /register in a fresh tab from a marketing email). Critical
 for the Fast by NoFee modal flow: Home → Modal → Register
 → Back should land back on the Home (modal closes) rather
 than wiping out the user's navigation context. */}
 <button
 type="button"
 onClick={() => {
 if (window.history.length > 1) {
 navigate(-1);
 } else {
 navigate('/');
 }
 }}
 className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-900 mb-5 text-sm transition-colors"
 data-testid="register-back-home"
 >
 <ArrowLeft className="w-4 h-4" /> {t('common.back')}
 </button>

 <div className="flex items-center justify-between mb-2">
 <RegisterLogo />
 <LanguageSwitcher variant="light" compact />
 </div>

 <h1 className="text-3xl sm:text-4xl font-extrabold text-neutral-900 tracking-tight mt-6">
 {t('register.title', { brand: 'NoFee' })}
 </h1>
 <p className="mt-2 text-neutral-600">
 {t('register.subtitle')}
 </p>

 <div className="mt-5 mb-3 h-1 w-12 rounded-full bg-gold-500" />

 <h2 className="text-xl font-bold text-neutral-900">{t('register.section_title')}</h2>
 <p className="text-neutral-500 text-sm mt-1 mb-5">
 {t('register.section_subtitle')}
 </p>

 {error && (
 <div
 className="p-3 bg-red-50 text-red-700 text-sm rounded-xl mb-5 border border-red-200 flex items-start gap-2"
 data-testid="register-error"
 >
 <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
 <span>{error}</span>
 </div>
 )}

 {goesToBilling && (
 <div className="mb-5" data-testid="register-stripe-trust">
 <StripeTrustBanner variant="card" />
 </div>
 )}

 {partnerInfo &&!partnerInfo.invalid && (
 <div
 className="mb-5 rounded-xl border border-amber-400/40 bg-gradient-to-br from-amber-50 to-yellow-50 p-4"
 data-testid="register-partner-banner"
 >
 <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-amber-700 mb-1">
 Partner referral
 </p>
 <p className="text-sm text-neutral-900 font-bold">
 Referred by <span className="text-amber-800">{partnerInfo.organization_name}</span>
 </p>
 <p className="text-xs text-neutral-600 mt-1">
 You get an exclusive <span className="font-semibold">45-day free trial</span> through this partnership.
 </p>
 </div>
 )}

 <form onSubmit={handleSubmit} noValidate className="space-y-3.5">
 <Field icon={Store}>
 <input type="text" required placeholder={t("register.restaurant_name")} value={form.restaurant_name}
 onChange={(e) => { setForm({...form, restaurant_name: e.target.value }); clearFieldError('restaurant_name'); }}
 className={`${input} ${fieldErrors.restaurant_name? 'border-red-400 focus:border-red-500 focus:ring-red-500/20': ''}`}
 data-testid="register-restaurant_name" />
 </Field>
 {fieldErrors.restaurant_name && (
 <p className="-mt-2 ml-1 text-[12px] font-semibold text-red-600 flex items-center gap-1" data-testid="error-restaurant_name">
 <AlertCircle className="w-3 h-3" /> {fieldErrors.restaurant_name}
 </p>
 )}
 <Field icon={User}>
 <input type="text" required placeholder={t("register.owner_name")} value={form.name}
 onChange={(e) => { setForm({...form, name: e.target.value }); clearFieldError('name'); }}
 className={`${input} ${fieldErrors.name? 'border-red-400 focus:border-red-500 focus:ring-red-500/20': ''}`}
 data-testid="register-name" />
 </Field>
 {fieldErrors.name && (
 <p className="-mt-2 ml-1 text-[12px] font-semibold text-red-600 flex items-center gap-1" data-testid="error-name">
 <AlertCircle className="w-3 h-3" /> {fieldErrors.name}
 </p>
 )}
 <Field icon={Mail}>
 <input type="email" required placeholder={t("register.email")} value={form.email}
 onChange={(e) => { setForm({...form, email: e.target.value }); clearFieldError('email'); }}
 className={`${input} ${fieldErrors.email? 'border-red-400 focus:border-red-500 focus:ring-red-500/20': ''}`}
 data-testid="register-email" />
 </Field>
 {fieldErrors.email && (
 <p className="-mt-2 ml-1 text-[12px] font-semibold text-red-600 flex items-center gap-1" data-testid="error-email">
 <AlertCircle className="w-3 h-3" /> {fieldErrors.email}
 </p>
 )}
 <Field icon={Lock}>
 <input type="password" required minLength={6} placeholder={t("register.password")} value={form.password}
 onChange={(e) => { setForm({...form, password: e.target.value }); clearFieldError('password'); }}
 className={`${input} ${fieldErrors.password? 'border-red-400 focus:border-red-500 focus:ring-red-500/20': ''}`}
 data-testid="register-password" />
 </Field>
 {fieldErrors.password && (
 <p className="-mt-2 ml-1 text-[12px] font-semibold text-red-600 flex items-center gap-1" data-testid="error-password">
 <AlertCircle className="w-3 h-3" /> {fieldErrors.password}
 </p>
 )}
 <Field icon={Phone}>
 <input type="tel" required placeholder="(617) 555-0123" value={form.phone}
 onChange={(e) => { setForm({...form, phone: formatUSPhone(e.target.value) }); clearFieldError('phone'); }}
 inputMode="tel"
 autoComplete="tel-national"
 maxLength={16}
 className={`${input} ${fieldErrors.phone? 'border-red-400 focus:border-red-500 focus:ring-red-500/20': ''}`}
 data-testid="register-phone" />
 </Field>
 {fieldErrors.phone? (
 <p className="-mt-2 ml-1 text-[12px] font-semibold text-red-600 flex items-center gap-1" data-testid="error-phone">
 <AlertCircle className="w-3 h-3" /> {fieldErrors.phone}
 </p>
 ): (
 <p className="-mt-2 ml-1 text-[11px] text-neutral-500" data-testid="register-phone-hint">
 {t('register.phone_hint', { defaultValue: 'US format: (617) 555-0123 — 10 digits.' })}
 </p>
 )}
 <Field icon={MapPin}>
 <input type="text" required placeholder={t("register.address")} value={form.address}
 onChange={(e) => { setForm({...form, address: e.target.value }); clearFieldError('address'); }}
 autoComplete="street-address"
 className={`${input} ${fieldErrors.address? 'border-red-400 focus:border-red-500 focus:ring-red-500/20': ''}`}
 data-testid="register-address" />
 </Field>
 {fieldErrors.address && (
 <p className="-mt-2 ml-1 text-[12px] font-semibold text-red-600 flex items-center gap-1" data-testid="error-address">
 <AlertCircle className="w-3 h-3" /> {fieldErrors.address}
 </p>
 )}
 <div className="grid grid-cols-3 gap-2.5">
 <Field icon={Hash}>
 <input
 type="text"
 required
 inputMode="numeric"
 pattern="\d{5}"
 maxLength={5}
 placeholder="ZIP"
 value={form.zip_code}
 onChange={(e) => { setForm({...form, zip_code: e.target.value.replace(/\D/g, '').slice(0, 5) }); clearFieldError('zip_code'); }}
 className={`${input} ${fieldErrors.zip_code? 'border-red-400 focus:border-red-500 focus:ring-red-500/20': ''}`}
 data-testid="register-zip"
 autoComplete="postal-code"
 />
 </Field>
 <Field icon={Building2}>
 <input
 type="text"
 required
 placeholder="City"
 value={form.city}
 onChange={(e) => { setForm({...form, city: e.target.value }); clearFieldError('city'); }}
 className={`${input} ${fieldErrors.city? 'border-red-400 focus:border-red-500 focus:ring-red-500/20': ''}`}
 data-testid="register-city"
 autoComplete="address-level2"
 />
 </Field>
 <Field icon={Map}>
 <select
 required
 value={form.state}
 onChange={(e) => { setForm({...form, state: e.target.value }); clearFieldError('state'); }}
 className={`${input} appearance-none bg-no-repeat bg-[right_0.75rem_center] bg-[length:14px] pr-9 ${fieldErrors.state? 'border-red-400 focus:border-red-500 focus:ring-red-500/20': ''}`}
 style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23737373' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")" }}
 data-testid="register-state"
 autoComplete="address-level1"
 >
 <option value="" disabled>State</option>
 {US_STATES.map((s) => (
 <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
 ))}
 </select>
 </Field>
 </div>
 {(fieldErrors.zip_code || fieldErrors.city || fieldErrors.state) && (
 <p className="-mt-1 ml-1 text-[12px] font-semibold text-red-600 flex items-center gap-1" data-testid="error-address-row">
 <AlertCircle className="w-3 h-3" />
 {[fieldErrors.zip_code, fieldErrors.city, fieldErrors.state].filter(Boolean).join(' · ')}
 </p>
 )}
 {zipLookup.status === 'loading' && (
 <p className="-mt-1 ml-1 text-[11px] text-neutral-500 flex items-center gap-1" data-testid="register-zip-checking">
 <Loader2 className="w-3 h-3 animate-spin" /> Verifying ZIP…
 </p>
 )}
 {zipLookup.status === 'ok' && (
 <p className="-mt-1 ml-1 text-[11px] text-green-600 font-semibold flex items-center gap-1" data-testid="register-zip-valid">
 <CheckCircle2 className="w-3 h-3" /> Resolved to {zipLookup.city}, {zipLookup.state}
 </p>
 )}
 {zipLookup.status === 'invalid' && (
 <p className="-mt-1 ml-1 text-[11px] text-amber-600 font-semibold flex items-center gap-1" data-testid="register-zip-invalid">
 <AlertCircle className="w-3 h-3" /> {zipLookup.error}
 </p>
 )}
 {addressMismatch && (
 <p className="-mt-1 ml-1 text-[11px] text-red-600 font-semibold flex items-start gap-1" data-testid="register-address-mismatch">
 <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" /> {addressMismatch.message}
 </p>
 )}
 <Field icon={Utensils}>
 <select value={form.cuisine_type}
 onChange={(e) => setForm({...form, cuisine_type: e.target.value })}
 className={`${input} appearance-none bg-no-repeat bg-[right_1rem_center] bg-[length:14px] pr-10`}
 style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23737373' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")" }}
 data-testid="register-cuisine_type">
 {cuisineOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
 </select>
 </Field>
 {form.cuisine_type === 'other' && (
 <>
 <Field icon={Utensils}>
 <input
 type="text"
 required
 placeholder={t('register.cuisine_other_placeholder', { defaultValue: 'Tell us your restaurant type (e.g., Vegan, Ethiopian, Steakhouse)' })}
 value={form.cuisine_type_other}
 onChange={(e) => { setForm({...form, cuisine_type_other: e.target.value }); clearFieldError('cuisine_type_other'); }}
 className={`${input} ${fieldErrors.cuisine_type_other? 'border-red-400 focus:border-red-500 focus:ring-red-500/20': ''}`}
 maxLength={60}
 data-testid="register-cuisine_type_other"
 />
 </Field>
 {fieldErrors.cuisine_type_other && (
 <p className="-mt-2 ml-1 text-[12px] font-semibold text-red-600 flex items-center gap-1" data-testid="error-cuisine_type_other">
 <AlertCircle className="w-3 h-3" /> {fieldErrors.cuisine_type_other}
 </p>
 )}
 </>
 )}
 <Field icon={Users}>
 <select value={form.how_heard}
 onChange={(e) => setForm({...form, how_heard: e.target.value })}
 className={`${input} appearance-none bg-no-repeat bg-[right_1rem_center] bg-[length:14px] pr-10`}
 style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23737373' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")" }}
 data-testid="register-how_heard">
 {howHeardOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
 </select>
 </Field>

 <details className="group" data-testid="register-referral-wrapper">
 <summary className="cursor-pointer list-none flex items-center gap-2 text-sm text-neutral-600 hover:text-gold-600 transition-colors py-1">
 <Gift className="w-4 h-4 text-gold-500" />
 <span className="font-medium">{t('register.rewards_code')}</span>
 <span className="text-xs text-neutral-400 group-open:hidden">({t('common.optional')})</span>
 </summary>
 <div className="mt-2">
 <Field icon={Gift}>
 <input type="text" placeholder={t("register.rewards_placeholder")} value={form.referral_code}
 onChange={(e) => setForm({...form, referral_code: e.target.value.toUpperCase() })}
 className={`${input} uppercase tracking-wide`} data-testid="register-referral_code" />
 </Field>
 {refChecking && <p className="mt-1.5 text-xs text-neutral-500">{t("register.checking_code")}</p>}
 {!refChecking && refStatus?.valid && (
 <p className="mt-1.5 text-xs text-green-600 font-semibold flex items-center gap-1.5" data-testid="register-referral-valid">
 <CheckCircle2 className="w-3.5 h-3.5" /> {t('register.referred_by', { name: refStatus.ambassador_name })}
 </p>
 )}
 {!refChecking && refStatus &&!refStatus.valid && form.referral_code && (
 <p className="mt-1.5 text-xs text-amber-600 font-semibold flex items-center gap-1.5" data-testid="register-referral-invalid">
 <AlertCircle className="w-3.5 h-3.5" /> {t('register.code_not_found')}
 </p>
 )}
 </div>
 </details>

 <label className="flex items-start gap-2 pt-2 cursor-pointer select-none">
 <input type="checkbox" checked={acceptedTerms}
 onChange={(e) => setAcceptedTerms(e.target.checked)}
 className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-gold-500 focus:ring-gold-500"
 data-testid="register-accept-terms" />
 <span className="text-xs text-neutral-600 leading-relaxed">
 {t('register.terms_agree')}{' '}
 <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-gold-600 font-semibold hover:text-gold-700 underline">{t("register.terms_of_service")}</a>,{' '}
 <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-gold-600 font-semibold hover:text-gold-700 underline">{t("register.privacy_policy")}</a>, {t('common.or')}{' '}
 <a href="/cookies" target="_blank" rel="noopener noreferrer" className="text-gold-600 font-semibold hover:text-gold-700 underline">{t("register.cookie_policy")}</a>.
 </span>
 </label>

 <button type="submit" disabled={loading}
 className="w-full mt-3 py-4 bg-gold-500 hover:bg-gold-400 active:bg-gold-600 text-black font-extrabold tracking-wide rounded-xl shadow-lg shadow-gold-500/30 transition-all disabled:opacity-60 disabled:cursor-not-allowed uppercase text-sm"
 data-testid="register-submit">
 {loading? t('register.submitting'): t('register.submit')}
 </button>

 <p className="text-center text-xs text-neutral-500 flex items-center justify-center gap-1.5 pt-1">
 <ShieldCheck className="w-3.5 h-3.5 text-neutral-400" />
 {t("register.secure_note")}
 </p>
 </form>

 <div className="mt-6 p-4 rounded-2xl bg-neutral-50 border border-neutral-200 flex items-start gap-3">
 <div className="w-10 h-10 rounded-xl bg-gold-100 text-gold-600 flex items-center justify-center flex-shrink-0">
 <ShieldCheck className="w-5 h-5" />
 </div>
 <div>
 <p className="font-bold text-neutral-900 text-sm">{t("register.commission_free_title")}</p>
 <p className="text-xs text-neutral-500 mt-0.5">
 {t("register.commission_free_sub")}
 </p>
 </div>
 </div>

 <p className="text-center text-sm text-neutral-500 mt-6">
 {t('register.already_have')}{' '}
 <Link to="/login" className="text-gold-600 font-semibold hover:text-gold-700">{t("common.sign_in")}</Link>
 </p>
 </div>
 </div>

 {/* RIGHT — Hero image (md+) — shows the FULL PNG, never crops faces/branding */}
 <div className="hidden md:block flex-1 relative bg-neutral-950 overflow-hidden">
 {/* Blurred ambient backdrop (premium frame — never empty bars) */}
 <div
 aria-hidden="true"
 className="absolute inset-0 bg-cover bg-center scale-110 blur-2xl opacity-50"
 style={{ backgroundImage: `url(${HERO_IMG})` }}
 />
 {/* Subtle dark vignette */}
 <div
 aria-hidden="true"
 className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_30%,rgba(0,0,0,0.55)_100%)]"
 />
 {/* Sharp foreground image — fully visible, centered, balanced padding */}
 <img
 src={HERO_IMG}
 alt="NoFee — More Orders. More Profit. No Fees."
 className="relative w-full h-full object-contain object-center p-4 lg:p-6 xl:p-8"
 data-testid="register-hero-image"
 />
 </div>
 </div>
 );
};

export default RegisterPage;
